import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  IconButton
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const PhoneNumbersSettingsDialog = ({ 
  open, 
  onClose, 
  onApply,
  enablePhoneActivation = false,
  hasPhoneNumbersCustomSettings = false
}) => {
  const { t } = useTranslation();
  
  // Local state for form values
  const [formValues, setFormValues] = useState({
    enablePhoneActivation: enablePhoneActivation,
    hasPhoneNumbersCustomSettings: hasPhoneNumbersCustomSettings
  });

  // Update local state when props change
  useEffect(() => {
    setFormValues({
      enablePhoneActivation: enablePhoneActivation,
      hasPhoneNumbersCustomSettings: hasPhoneNumbersCustomSettings
    });
  }, [enablePhoneActivation, hasPhoneNumbersCustomSettings]);

  const handleClose = () => {
    // Reset to original values
    setFormValues({
      enablePhoneActivation: enablePhoneActivation,
      hasPhoneNumbersCustomSettings: hasPhoneNumbersCustomSettings
    });
    onClose();
  };

  const handleApply = () => {
    const settingsToSave = {
      enablePhoneActivation: formValues.enablePhoneActivation,
      hasPhoneNumbersCustomSettings: formValues.enablePhoneActivation // Set to true if activation is enabled
    };
    
    onApply(settingsToSave);
  };

  const handleCheckboxChange = (field) => (event) => {
    setFormValues(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1,
          borderBottom: '1px solid rgba(0,0,0,0.08)'
        }}
      >
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {t('calculator.phoneNumbersSettings', 'Phone Numbers Settings')}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: 'text.secondary' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

          <FormControlLabel
            control={
              <Checkbox
                checked={formValues.enablePhoneActivation}
                onChange={handleCheckboxChange('enablePhoneActivation')}
                sx={{
                  color: '#00C853',
                  '&.Mui-checked': {
                    color: '#00C853',
                  }
                }}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t('calculator.enablePhoneActivation', 'Enable Phone Number Activation Fees')}
              </Typography>
            }
            sx={{ alignItems: 'center', ml: 0, mt: 1 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button 
          onClick={handleClose}
          sx={{ 
            color: 'text.secondary',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
          }}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          onClick={handleApply}
          variant="contained"
          sx={{ 
            backgroundColor: '#00C853',
            '&:hover': { backgroundColor: '#00A040' },
            minWidth: 100
          }}
        >
          {t('common.apply', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PhoneNumbersSettingsDialog;